import { useState, useEffect, useCallback } from 'react';
import { Hub } from 'aws-amplify/utils';
import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  signOut as amplifySignOut,
} from 'aws-amplify/auth';
import { supabase } from '@/config/supabase';
import config, { shouldApplyAuthBypass, suppressAuthBypassAfterSignOut } from '@/config/environment';
import {
  ensureAmplifyConfigured,
  isCognitoConfigured,
  loadCognitoUserAndSession,
} from '@/config/cognitoAuth';
import type { AppSession, AppUser, AuthResult } from '@/types/appAuth';
import type { Session, User, AuthResponse } from '@supabase/supabase-js';

const isPhone = (input: string) => {
  return /^\+?\d{10,15}$/.test(input.trim());
};

function toE164Indian(mobile: string): string {
  const d = mobile.replace(/\D/g, '');
  if (d.length === 10) return `+91${d}`;
  if (mobile.trim().startsWith('+')) return mobile.trim();
  return `+${d}`;
}

function makeAliasSafeUsername(email: string): string {
  const local = email.split('@')[0] || 'user';
  const safe = local.toLowerCase().replace(/[^a-z0-9._-]/g, '') || 'user';
  const suffix = Math.random().toString(36).slice(2, 8);
  return `${safe}_${suffix}`;
}

export const useAuth = () => {
  const bypassEmail = config.VITE_AUTH_BYPASS_EMAIL || 'admin@bujji.in';

  const [session, setSession] = useState<AppSession | Session | null>(null);
  const [user, setUser] = useState<AppUser | User | null>(null);
  const [loading, setLoading] = useState(true);

  const createBypassAuth = () => {
    const now = new Date().toISOString();
    const bypassUser = {
      id: `bypass-${bypassEmail}`,
      email: bypassEmail,
      aud: 'authenticated',
      role: 'authenticated',
      app_metadata: { provider: 'temporary-bypass' },
      user_metadata: { full_name: 'Temporary Admin' },
      created_at: now,
      updated_at: now,
    } as unknown as AppUser;

    const bypassSession = {
      access_token: 'temporary-auth-bypass',
      refresh_token: 'temporary-auth-bypass',
      token_type: 'bearer',
      expires_in: 60 * 60 * 24,
      expires_at: Math.floor(Date.now() / 1000) + 60 * 60 * 24,
      user: bypassUser,
    } as unknown as AppSession;

    return { bypassUser, bypassSession };
  };

  const syncCognito = useCallback(async () => {
    const out = await loadCognitoUserAndSession();
    if (out) {
      setUser(out.user);
      setSession(out.session);
    } else {
      setUser(null);
      setSession(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (shouldApplyAuthBypass()) {
      const { bypassUser, bypassSession } = createBypassAuth();
      setUser(bypassUser);
      setSession(bypassSession);
      setLoading(false);
      return;
    }

    if (isCognitoConfigured()) {
      ensureAmplifyConfigured();
      let cancelled = false;

      const run = async () => {
        const out = await loadCognitoUserAndSession();
        if (cancelled) return;
        if (out) {
          setUser(out.user);
          setSession(out.session);
        } else {
          setUser(null);
          setSession(null);
        }
        setLoading(false);
      };

      void run();

      const remove = Hub.listen('auth', ({ payload }) => {
        const ev = payload?.event;
        if (ev === 'signedIn' || ev === 'signedOut' || ev === 'tokenRefresh') {
          void run();
        }
      });

      return () => {
        cancelled = true;
        remove();
      };
    }

    let cancelled = false;

    const checkSession = async () => {
      const {
        data: { session: s },
      } = await supabase.auth.getSession();
      if (cancelled) return;
      if (s) {
        setSession(s as Session);
        setUser(s.user as User);
      } else {
        setSession(null);
        setUser(null);
      }
      setLoading(false);
    };

    void checkSession();

    const { data: authListener } = supabase.auth.onAuthStateChange((_event: string, s: Session | null) => {
      if (s) {
        setSession(s);
        setUser(s.user as User);
      } else {
        setUser(null);
        setSession(null);
      }
    });

    return () => {
      cancelled = true;
      authListener.subscription.unsubscribe();
    };
  }, []);

  const signUp = async (
    email: string,
    password: string,
    profile?: { full_name?: string; phone?: string }
  ): Promise<AuthResult | AuthResponse> => {
    if (shouldApplyAuthBypass()) {
      const { bypassUser, bypassSession } = createBypassAuth();
      setUser(bypassUser);
      setSession(bypassSession);
      return { data: { user: bypassUser, session: bypassSession }, error: null };
    }

    if (isCognitoConfigured()) {
      ensureAmplifyConfigured();
      setLoading(true);
      try {
        const attrs: Record<string, string> = { email };
        if (profile?.full_name) attrs.name = profile.full_name;
        if (profile?.phone) attrs.phone_number = toE164Indian(profile.phone);

        let res;
        try {
          res = await amplifySignUp({
            username: email.trim().toLowerCase(),
            password,
            options: { userAttributes: attrs },
          });
        } catch (rawErr) {
          const msg = rawErr instanceof Error ? rawErr.message : String(rawErr);
          // Some pools use email as an alias (username must be non-email).
          if (/Username cannot be of email format/i.test(msg)) {
            res = await amplifySignUp({
              username: makeAliasSafeUsername(email),
              password,
              options: { userAttributes: attrs },
            });
          } else {
            throw rawErr;
          }
        }

        if (!res.isSignUpComplete && res.nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
          setLoading(false);
          return {
            data: { user: null, session: null },
            error: new Error(
              'Check your email for a confirmation code, then sign in. If the pool uses auto-verified email, try signing in now.'
            ),
          };
        }

        await syncCognito();
        setLoading(false);
        const out = await loadCognitoUserAndSession();
        return {
          data: {
            user: out?.user ?? null,
            session: out?.session ?? null,
          },
          error: null,
        };
      } catch (e: unknown) {
        setLoading(false);
        const err = e instanceof Error ? e : new Error(String(e));
        return { data: { user: null, session: null }, error: err };
      }
    }

    setLoading(true);
    const result = await supabase.auth.signUp({
      email,
      password,
    });
    setLoading(false);
    return result as AuthResponse;
  };

  const signIn = async (identifier: string, password: string): Promise<AuthResult | AuthResponse> => {
    if (shouldApplyAuthBypass()) {
      const { bypassUser, bypassSession } = createBypassAuth();
      setUser(bypassUser);
      setSession(bypassSession);
      return { data: { user: bypassUser, session: bypassSession }, error: null };
    }

    if (isCognitoConfigured()) {
      ensureAmplifyConfigured();
      setLoading(true);
      const username = isPhone(identifier) ? toE164Indian(identifier) : identifier.trim().toLowerCase();
      try {
        await amplifySignIn({ username, password });
        await syncCognito();
        setLoading(false);
        const out = await loadCognitoUserAndSession();
        return {
          data: { user: out?.user ?? null, session: out?.session ?? null },
          error: null,
        };
      } catch (e: unknown) {
        setLoading(false);
        const err = e instanceof Error ? e : new Error(String(e));
        return { data: { user: null, session: null }, error: err };
      }
    }

    setLoading(true);
    const supabasePayload = isPhone(identifier)
      ? { phone: identifier, password }
      : { email: identifier, password };

    let result: AuthResponse;
    try {
      result = await supabase.auth.signInWithPassword(supabasePayload as any);
    } catch (e: any) {
      result = {
        data: { user: null, session: null },
        error: e,
      } as unknown as AuthResponse;
    }

    const { data } = result as any;
    if (data?.user) {
      setUser(data.user as User);
      setSession(data.session as Session);
    }
    setLoading(false);
    return result;
  };

  const signOut = async () => {
    const leavingTemporaryBypass =
      user &&
      typeof (user as AppUser).id === 'string' &&
      (user as AppUser).id.startsWith('bypass-');
    if (leavingTemporaryBypass) {
      suppressAuthBypassAfterSignOut();
      setUser(null);
      setSession(null);
      setLoading(false);
      return { error: null };
    }

    setLoading(true);
    try {
      if (isCognitoConfigured()) {
        ensureAmplifyConfigured();
        await amplifySignOut();
      } else {
        await supabase.auth.signOut();
      }
    } catch {
      /* still clear local state */
    }
    setUser(null);
    setSession(null);
    setLoading(false);
    return { error: null };
  };

  return {
    session,
    user,
    loading,
    signUp,
    signIn,
    signOut,
  };
};
